import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, AlertTriangle } from "lucide-react";

/**
 * Shows a promotional banner above every owner page when the user is on a trial
 * plan (or their trial has expired and no paid plan is active).
 *
 * Intentionally fails open: if the API call errors, nothing is rendered.
 */
export function TrialBanner() {
  const [sub, setSub] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/subscription")
      .then((r) => { if (alive) setSub(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!sub) return null;
  const s = sub.subscription;
  // CASE 1 — expired trial or no active plan at all
  if (!s) {
    return (
      <div className="bg-destructive text-white" data-testid="trial-banner-expired">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="font-semibold">Your free trial has ended.</span>
            <span className="hidden sm:inline text-white/85">Pick a plan to keep publishing your menu.</span>
          </div>
          <Link
            to="/dashboard/billing"
            className="inline-flex items-center gap-1 bg-white text-destructive font-bold text-xs px-4 py-1.5 rounded-full hover:bg-white/90"
            data-testid="trial-banner-upgrade-cta"
          >
            Upgrade now →
          </Link>
        </div>
      </div>
    );
  }
  // CASE 2 — trial still active
  if (!s.is_trial) return null;
  const daysLeft = sub.trial_days_left;
  const hoursLeft = daysLeft != null ? Math.max(0, Math.round(daysLeft * 24)) : null;
  const timeLabel =
    hoursLeft == null ? "" :
    hoursLeft >= 24 ? `${Math.round(hoursLeft / 24)} day${Math.round(hoursLeft / 24) === 1 ? "" : "s"} left` :
    hoursLeft <= 1 ? "Less than an hour left" :
    `${hoursLeft} hours left`;

  return (
    <div className="bg-gradient-to-r from-brand-orange to-[#FF8A3D] text-white" data-testid="trial-banner-active">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 shrink-0" />
          <span className="font-semibold">You&rsquo;re on the 1-day free trial</span>
          {timeLabel && <span className="text-white/90">· {timeLabel}</span>}
        </div>
        <Link
          to="/dashboard/billing"
          className="inline-flex items-center gap-1 bg-white text-brand-orange font-bold text-xs px-4 py-1.5 rounded-full hover:bg-white/90"
          data-testid="trial-banner-upgrade-cta"
        >
          Upgrade now →
        </Link>
      </div>
    </div>
  );
}
